import { useState, useEffect, useRef } from 'react'
import { custodianDataService, type CombinedCustodianData } from '../../../services/custodianDataService'
import { convertAndUploadJsonToCsv, type CsvUploadResponse } from '../../../services/csvDataProcessor'
import '../../admin/workflow/tabs/BanksInfoTab.css'
import './CustodianMergedView.css'

// Global state to prevent multiple instances from making duplicate calls
let globalCustodianData: CombinedCustodianData | null = null
let globalCustodianIsLoading = false
let globalCustodianLoadPromise: Promise<CombinedCustodianData> | null = null

type DataTableType = 'clients' | 'accounts' | 'holdings' | 'portfolios' | 'securities' | 'prices' | 'contact_info' | 'merged'

const CustodianInfoDisplay = () => {
  const [custodianData, setCustodianData] = useState<CombinedCustodianData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeDataType, setActiveDataType] = useState<DataTableType>('clients')
  const [exportLoading, setExportLoading] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState<CsvUploadResponse | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const hasLoadedDataRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Check if we already have global data
    if (globalCustodianData) {
      console.log('📋 [CustodianInfo] Using cached global data')
      setCustodianData(globalCustodianData)
      setLoading(false)
      hasLoadedDataRef.current = true
      return
    }

    // Check if already loading globally
    if (globalCustodianIsLoading) {
      console.log('⏳ [CustodianInfo] Already loading globally, waiting...')
      if (globalCustodianLoadPromise) {
        globalCustodianLoadPromise.then(data => {
          setCustodianData(data)
          setLoading(false)
          hasLoadedDataRef.current = true
        }).catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to load custodian data')
          setLoading(false)
        })
      }
      return
    }

    // Prevent duplicate API calls and abort any existing requests
    if (hasLoadedDataRef.current) return

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()

    const loadCustodianData = async (): Promise<CombinedCustodianData> => {
      try {
        setLoading(true)
        setError(null)
        globalCustodianIsLoading = true

        console.log('📡 [CustodianInfo] Starting data fetch for custodians...')

        const data = await custodianDataService.getCombinedCustodianData()

        // Set both local and global state
        setCustodianData(data)
        globalCustodianData = data

        console.log('✅ [CustodianInfo] Custodian data loaded successfully', {
          custodian_a: data.custodianA ? 'loaded' : 'failed',
          custodian_b: data.custodianB ? 'loaded' : 'failed'
        })

        // Mark data as loaded to prevent duplicate calls
        hasLoadedDataRef.current = true

        return data

      } catch (err) {
        // Don't set error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('🚫 [CustodianInfo] Request was aborted')
          throw err
        }
        const errorMessage = err instanceof Error ? err.message : 'Failed to load custodian data'
        setError(errorMessage)
        console.error('❌ [CustodianInfo] Error loading custodian data:', err)
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
        globalCustodianIsLoading = false
        globalCustodianLoadPromise = null
      }
    }

    // Set up global promise and execute
    globalCustodianLoadPromise = loadCustodianData()
    globalCustodianLoadPromise.then(() => {
      console.log('📋 [CustodianInfo] Global promise resolved with data')
    }).catch(err => {
      console.error('❌ [CustodianInfo] Global promise rejected:', err)
    })

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        console.log('🧹 [CustodianInfo] Cleanup - aborting requests')
        abortControllerRef.current.abort()
      }
    }
  }, []) // Empty dependency array - only run once on mount

  const fetchCustodianData = async () => {
    // Reset global state to force refresh
    globalCustodianData = null
    globalCustodianIsLoading = false
    globalCustodianLoadPromise = null
    hasLoadedDataRef.current = false

    try {
      setLoading(true)
      setError(null)
      const data = await custodianDataService.getCombinedCustodianData()
      setCustodianData(data)
      globalCustodianData = data
      hasLoadedDataRef.current = true
    } catch (err: any) {
      setError(err.message || 'Failed to fetch custodian data')
    } finally {
      setLoading(false)
    }
  }

  // Handle data type switching
  const handleDataTypeSwitch = (dataType: DataTableType) => {
    setActiveDataType(dataType)
  }

  // Handle CSV export for different custodians
  const handleExport = async (custodianType: 'custodian_a' | 'custodian_b') => {
    if (!custodianData) return

    try {
      setExportLoading(custodianType)
      setExportError(null)
      setExportSuccess(null)

      let dataToExport: any[] = []
      let fileName = ''

      // Export ALL data from the API response (create meaningful relationships)
      if (custodianType === 'custodian_a') {
        const tables = custodianData.custodianA?.data.tables
        if (tables) {
          const clients = tables.clients || []
          const accounts = tables.accounts || []
          const contacts = tables.contact_info || []
          const holdings = tables.holdings || []
          const portfolios = tables.portfolio_summaries || []
          const securities = tables.securities_master || []
          const prices = tables.security_prices || []

          // Create one row per holding with all related information
          const holdingsWithDetails = holdings.map(holding => {
            const account = accounts.find(a => a.id === holding.account_id)
            const client = clients.find(c => c.client_identifier === account?.client_identifier)
            const contact = contacts.find(c => c.client_identifier === client?.client_identifier)
            const security = securities.find(s => s.id === holding.security_id)
            const price = prices.find(p => p.security_id === holding.security_id)
            const portfolio = portfolios.find(p => p.account_id === holding.account_id)

            return {
              // Client information
              client_identifier: client?.client_identifier || '',
              first_name: client?.first_name || '',
              last_name: client?.last_name || '',
              social_security_number: contact?.social_security_number || '',
              date_of_birth: client?.date_of_birth || '',
              email_address: contact?.email_address || '',
              
              designation: client?.designation || '',
              company: client?.company || '',
              joining_date: client?.joining_date || '',
              is_active: client?.is_active || '',
              risk_profile: client?.risk_profile || '',
              emergency_contact_name: contact?.emergency_contact_name || '',
              address_line_1: contact?.address_line_1 || '',
              address_line_2: contact?.address_line_2 || '',
              street: contact?.street || '',
              city: contact?.city || '',
              state: contact?.state || '',
              country: contact?.country || '',
              mailing_zip_code: contact?.mailing_zip_code || '',
              phone_number: contact?.phone_number || '',

              // Account information
              account_identifier: account?.account_identifier || '',
              account_amount: account?.amount || '',
              account_status: account?.status || '',
              account_type: account?.account_type || '',
              account_name: account?.account_name || '',

              // Security/Price information
              AsOfDate: price?.price_date || '',
              ticker_symbol: security?.symbol || '',
              cusip_identifier: security?.cusip || '',
              SecurityName: security?.security_name || '',
              AssetCategory: security?.asset_class || '',
              AssetSubClass: security?.asset_sub_class || '',
              SectorClassification: security?.sector || '',

              // Holdings information
              HeldQuantity: holding?.quantity || '',
              MarketPrice: holding?.current_price || '',
              CurrentMarketValue: holding?.market_value || '',
              PriorDayClose: price?.close_price || '',
              DayHighPrice: price?.high_price || '',
              DayLowPrice: price?.low_price || '',
              UnrealizedGainLoss: holding?.unrealized_gain_loss || '',
              OriginalCostBasis: holding?.total_cost || '',
              AcquisitionDate: holding?.acquisition_date || '',

              // Portfolio information
              TotalPortfolioValue: portfolio?.total_market_value || '',
              TotalCostBasis: portfolio?.total_cost_basis || '',
              TotalUnrealizedGainLoss: portfolio?.total_unrealized_gain_loss || '',
              HoldingsCount: portfolio?.holdings_count || '',
              EquityPercentage: portfolio?.equity_percentage || '',
              FixedIncomePercentage: portfolio?.fixed_income_percentage || '',
              CashPercentage: portfolio?.cash_percentage || ''
            }
          })

          // Add standalone records (clients without holdings, etc.)
          const clientsWithoutHoldings = clients
            .filter(client => !accounts.some(acc => acc.client_identifier === client.client_identifier && holdings.some(h => h.account_id === acc.id)))
            .map(client => {
              const contact = contacts.find(c => c.client_identifier === client.client_identifier)
              return {
                // Client information
                client_identifier: client?.client_identifier || '',
                first_name: client?.first_name || '',
                last_name: client?.last_name || '',
                social_security_number: contact?.social_security_number || '',
                date_of_birth: client?.date_of_birth || '',
                email_address: contact?.email_address || '',

                // Empty account information
                account_identifier: '',
                account_amount: '',
                account_status: '',
                account_type: '',
                account_name: '',

                // Empty security/price information
                AsOfDate: '',
                ticker_symbol: '',
                cusip_identifier: '',
                SecurityName: '',
                AssetCategory: '',
                AssetSubClass: '',
                SectorClassification: '',

                // Empty holdings information
                HeldQuantity: '',
                MarketPrice: '',
                CurrentMarketValue: '',
                PriorDayClose: '',
                DayHighPrice: '',
                DayLowPrice: '',
                UnrealizedGainLoss: '',
                OriginalCostBasis: '',
                AcquisitionDate: '',

                // Empty portfolio information
                TotalPortfolioValue: '',
                TotalCostBasis: '',
                TotalUnrealizedGainLoss: '',
                HoldingsCount: '',
                EquityPercentage: '',
                FixedIncomePercentage: '',
                CashPercentage: ''
              }
            })

          dataToExport = [...holdingsWithDetails, ...clientsWithoutHoldings]
        }
        fileName = `custodian_a_complete_${new Date().toISOString().split('T')[0]}`

      } else if (custodianType === 'custodian_b') {
        const tables = custodianData.custodianB?.data.tables
        if (tables) {
          const clients = tables.clients || []
          const accounts = tables.accounts || []
          const contacts = tables.contact_info || []
          const holdings = tables.holdings || []
          const portfolios = tables.portfolio_summaries || []
          const securities = tables.securities_master || []
          const prices = tables.security_prices || []

          // Create one row per holding with all related information
          const holdingsWithDetails = holdings.map(holding => {
            const account = accounts.find(a => a.id === holding.account_id)
            const client = clients.find(c => c.client_identifier === account?.client_identifier)
            const contact = contacts.find(c => c.client_identifier === client?.client_identifier)
            const security = securities.find(s => s.id === holding.security_id)
            const price = prices.find(p => p.security_id === holding.security_id)
            const portfolio = portfolios.find(p => p.account_id === holding.account_id)

            return {
              // Client information
              client_identifier: client?.client_identifier || '',
              first_name: client?.first_name || '',
              last_name: client?.last_name || '',
              social_security_number: contact?.social_security_number || '',
              date_of_birth: client?.date_of_birth || '',
              email_address: contact?.email_address || '',
              
              designation: client?.designation || '',
              company: client?.company || '',
              joining_date: client?.joining_date || '',
              is_active: client?.is_active || '',
              risk_profile: client?.risk_profile || '',
              emergency_contact_name: contact?.emergency_contact_name || '',
              address_line_1: contact?.address_line_1 || '',
              address_line_2: contact?.address_line_2 || '',
              street: contact?.street || '',
              city: contact?.city || '',
              state: contact?.state || '',
              country: contact?.country || '',
              mailing_zip_code: contact?.mailing_zip_code || '',
              phone_number: contact?.phone_number || '',

              // Account information
              account_identifier: account?.account_identifier || '',
              account_amount: account?.amount || '',
              account_status: account?.status || '',
              account_type: account?.account_type || '',
              account_name: account?.account_name || '',

              // Security/Price information
              AsOfDate: price?.price_date || '',
              ticker_symbol: security?.symbol || '',
              cusip_identifier: security?.cusip || '',
              SecurityName: security?.security_name || '',
              AssetCategory: security?.asset_class || '',
              AssetSubClass: security?.asset_sub_class || '',
              SectorClassification: security?.sector || '',

              // Holdings information
              HeldQuantity: holding?.quantity || '',
              MarketPrice: holding?.current_price || '',
              CurrentMarketValue: holding?.market_value || '',
              PriorDayClose: price?.close_price || '',
              DayHighPrice: price?.high_price || '',
              DayLowPrice: price?.low_price || '',
              UnrealizedGainLoss: holding?.unrealized_gain_loss || '',
              OriginalCostBasis: holding?.total_cost || '',
              AcquisitionDate: holding?.acquisition_date || '',

              // Portfolio information
              TotalPortfolioValue: portfolio?.total_market_value || '',
              TotalCostBasis: portfolio?.total_cost_basis || '',
              TotalUnrealizedGainLoss: portfolio?.total_unrealized_gain_loss || '',
              HoldingsCount: portfolio?.holdings_count || '',
              EquityPercentage: portfolio?.equity_percentage || '',
              FixedIncomePercentage: portfolio?.fixed_income_percentage || '',
              CashPercentage: portfolio?.cash_percentage || ''
            }
          })

          // Add standalone records (clients without holdings, etc.)
          const clientsWithoutHoldings = clients
            .filter(client => !accounts.some(acc => acc.client_identifier === client.client_identifier && holdings.some(h => h.account_id === acc.id)))
            .map(client => {
              const contact = contacts.find(c => c.client_identifier === client.client_identifier)
              return {
                // Client information
                client_identifier: client?.client_identifier || '',
                first_name: client?.first_name || '',
                last_name: client?.last_name || '',
                social_security_number: contact?.social_security_number || '',
                date_of_birth: client?.date_of_birth || '',
                email_address: contact?.email_address || '',

                // Empty account information
                account_identifier: '',
                account_amount: '',
                account_status: '',
                account_type: '',
                account_name: '',

                // Empty security/price information
                AsOfDate: '',
                ticker_symbol: '',
                cusip_identifier: '',
                SecurityName: '',
                AssetCategory: '',
                AssetSubClass: '',
                SectorClassification: '',

                // Empty holdings information
                HeldQuantity: '',
                MarketPrice: '',
                CurrentMarketValue: '',
                PriorDayClose: '',
                DayHighPrice: '',
                DayLowPrice: '',
                UnrealizedGainLoss: '',
                OriginalCostBasis: '',
                AcquisitionDate: '',

                // Empty portfolio information
                TotalPortfolioValue: '',
                TotalCostBasis: '',
                TotalUnrealizedGainLoss: '',
                HoldingsCount: '',
                EquityPercentage: '',
                FixedIncomePercentage: '',
                CashPercentage: ''
              }
            })

          dataToExport = [...holdingsWithDetails, ...clientsWithoutHoldings]
        }
        fileName = `custodian_b_complete_${new Date().toISOString().split('T')[0]}`
      }

      if (dataToExport.length === 0) {
        throw new Error('No data available for export')
      }

      const result = await convertAndUploadJsonToCsv(dataToExport, fileName, undefined, custodianType)
      setExportSuccess(result)

    } catch (err: any) {
      setExportError(err.message || 'Export failed')
    } finally {
      setExportLoading(null)
    }
  }

  // Clear export notifications
  const clearExportNotifications = () => {
    setExportSuccess(null)
    setExportError(null)
  }

  if (loading) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div
            style={{
              margin: '0 auto 16px',
              width: '32px',
              height: '32px',
              border: '3px solid #f3f4f6',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}
          ></div>
          <p style={{ color: '#6b7280' }}>Loading custodian data...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#dc2626', marginBottom: '8px' }}>Error Loading Custodian Data</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={fetchCustodianData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!custodianData) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h3>No Custodian Data Available</h3>
        </div>
      </div>
    )
  }

  if (!custodianData.custodianA && !custodianData.custodianB) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔒</div>
          <h3>No Custodian Data Available</h3>
        </div>
      </div>
    )
  }

  // Get current data based on active data type
  const getCustodianAData = () => {
    if (!custodianData.custodianA) return []
    const tables = custodianData.custodianA.data.tables
    switch (activeDataType) {
      case 'clients': return tables.clients || []
      case 'accounts': return tables.accounts || []
      case 'holdings': return tables.holdings || []
      case 'portfolios': return tables.portfolio_summaries || []
      case 'securities': return tables.securities_master || []
      case 'prices': return tables.security_prices || []
      case 'contact_info': return tables.contact_info || []
      default: return []
    }
  }

  const getCustodianBData = () => {
    if (!custodianData.custodianB) return []
    const tables = custodianData.custodianB.data.tables
    switch (activeDataType) {
      case 'clients': return tables.clients || []
      case 'accounts': return tables.accounts || []
      case 'holdings': return tables.holdings || []
      case 'portfolios': return tables.portfolio_summaries || []
      case 'securities': return tables.securities_master || []
      case 'prices': return tables.security_prices || []
      case 'contact_info': return tables.contact_info || []
      default: return []
    }
  }

  const custodianAData = getCustodianAData()
  const custodianBData = getCustodianBData()

  // Get all available fields from both custodians for comprehensive comparison
  const getAllFieldsFromData = () => {
    const allFields = new Set<string>()

    // Add fields from main datasets
    ;[...custodianAData, ...custodianBData].forEach(item => {
      if (item) {
        Object.keys(item).forEach(key => {
          // Only skip truly internal system fields - keep all business fields
          if (!['id', 'created_at', 'updated_at', 'deleted_at', 'created_by', 'updated_by', 'deleted_by'].includes(key)) {
            allFields.add(key)
          }
        })
      }
    })

    // For client data, also add fields from contact_info to ensure comprehensive comparison
    if (activeDataType === 'clients') {
      const custodianAContacts = custodianData.custodianA?.data.tables.contact_info || []
      const custodianBContacts = custodianData.custodianB?.data.tables.contact_info || []

      ;[...custodianAContacts, ...custodianBContacts].forEach(contact => {
        if (contact) {
          Object.keys(contact).forEach(key => {
            // Add contact fields, excluding system fields and client_id (duplicate of client_identifier)
            if (!['id', 'client_id', 'created_at', 'updated_at', 'deleted_at', 'created_by', 'updated_by', 'deleted_by'].includes(key)) {
              allFields.add(key)
            }
          })
        }
      })
    }

    // Convert to array and sort for consistent ordering
    // Prioritize important business fields first based on data type
    const fieldsArray = Array.from(allFields)
    let priorityFields: string[] = []

    switch (activeDataType) {
      case 'clients':
        priorityFields = [
          'client_identifier', 'first_name', 'last_name', 'designation', 'company',
          'date_of_birth', 'is_active', 'risk_profile',
          'email_address', 'phone_number', 'emergency_contact_name',
          'address_line_1', 'address_line_2', 'city', 'state', 'country'
        ]
        break
      case 'accounts':
        priorityFields = [
          'account_identifier', 'client_identifier', 'account_type', 'account_name',
          'amount', 'status'
        ]
        break
      case 'holdings':
        priorityFields = [
          'account_id', 'security_id', 'quantity', 'unit_cost', 'current_price',
          'market_value', 'unrealized_gain_loss', 'total_cost', 'acquisition_date'
        ]
        break
      case 'portfolios':
        priorityFields = [
          'client_identifier', 'account_id', 'total_market_value', 'total_cost_basis',
          'total_unrealized_gain_loss', 'holdings_count', 'equity_percentage'
        ]
        break
      case 'securities':
        priorityFields = [
          'symbol', 'security_name', 'security_type', 'asset_class', 'asset_sub_class',
          'sector', 'cusip', 'is_active'
        ]
        break
      case 'prices':
        priorityFields = [
          'security_id', 'price_date', 'open_price', 'high_price', 'low_price',
          'close_price', 'volume'
        ]
        break
      case 'contact_info':
        priorityFields = [
          'client_identifier', 'email_address', 'phone_number', 'emergency_contact_name',
          'address_line_1', 'address_line_2', 'city', 'state', 'country', 'social_security_number'
        ]
        break
      default:
        priorityFields = []
    }

    const priorityFieldsInData = priorityFields.filter(field => fieldsArray.includes(field))
    const remainingFields = fieldsArray.filter(field => !priorityFields.includes(field)).sort()

    return [...priorityFieldsInData, ...remainingFields]
  }

  // Format field name for display
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Format field value for display
  const formatFieldValue = (value: any, fieldName: string): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }

    // Special formatting for specific field types
    if ((fieldName.includes('amount') || fieldName.includes('price') || fieldName.includes('value') || fieldName.includes('cost')) && typeof value === 'string') {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        return `$${numValue.toLocaleString()}`
      }
    }

    if (fieldName === 'is_active' && typeof value === 'boolean') {
      return value ? 'Active' : 'Inactive'
    }

    if (fieldName === 'social_security_number' && typeof value === 'string') {
      return value.length > 4 ? '***-**-' + value.slice(-4) : value
    }

    // Format dates nicely
    if ((fieldName.includes('date') || fieldName.includes('_at')) && typeof value === 'string') {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }
      } catch (e) {
        // If date parsing fails, return original value
      }
    }

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    // Handle arrays (in case any fields contain arrays)
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Empty'
    }

    // Handle objects (stringify them)
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }

    // Format percentages
    if (fieldName.includes('percentage') && typeof value === 'number') {
      return `${value}%`
    }

    return String(value)
  }

  // Get comprehensive row data for comparison
  const getComprehensiveRowData = (custodianAItem: any, custodianBItem: any) => {
    const allFields = getAllFieldsFromData()

    // For contact info, we need to merge data
    let custodianAContactInfo = null
    let custodianBContactInfo = null

    if (activeDataType === 'clients') {
      custodianAContactInfo = custodianData.custodianA?.data.tables.contact_info?.find(
        (contact: any) => contact.client_identifier === custodianAItem?.client_identifier
      )
      custodianBContactInfo = custodianData.custodianB?.data.tables.contact_info?.find(
        (contact: any) => contact.client_identifier === custodianBItem?.client_identifier
      )
    }

    return allFields.map(field => {
      let custodianAValue = custodianAItem?.[field as keyof typeof custodianAItem]
      let custodianBValue = custodianBItem?.[field as keyof typeof custodianBItem]

      // For client data, check contact info if field not found in main record
      if (activeDataType === 'clients') {
        if (custodianAValue === undefined && custodianAContactInfo && field in custodianAContactInfo) {
          custodianAValue = (custodianAContactInfo as any)[field]
        }
        if (custodianBValue === undefined && custodianBContactInfo && field in custodianBContactInfo) {
          custodianBValue = (custodianBContactInfo as any)[field]
        }
      }

      const formattedCustodianAValue = formatFieldValue(custodianAValue, field)
      const formattedCustodianBValue = formatFieldValue(custodianBValue, field)

      return {
        field,
        displayName: formatFieldName(field),
        custodianAValue: formattedCustodianAValue,
        custodianBValue: formattedCustodianBValue,
        isDifferent: formattedCustodianAValue !== formattedCustodianBValue &&
                    formattedCustodianAValue !== 'N/A' &&
                    formattedCustodianBValue !== 'N/A'
      }
    })
  }

  // Render merged view combining holdings, portfolio, and securities data
  const renderMergedView = () => {
    if (!custodianData) return null

    const custodianAClients = custodianData.custodianA?.data.tables.clients || []
    const custodianBClients = custodianData.custodianB?.data.tables.clients || []

    const custodianAHoldings = custodianData.custodianA?.data.tables.holdings || []
    const custodianBHoldings = custodianData.custodianB?.data.tables.holdings || []

    const custodianAPortfolios = custodianData.custodianA?.data.tables.portfolio_summaries || []
    const custodianBPortfolios = custodianData.custodianB?.data.tables.portfolio_summaries || []

    const custodianASecurities = custodianData.custodianA?.data.tables.securities_master || []
    const custodianBSecurities = custodianData.custodianB?.data.tables.securities_master || []

    const custodianAAccounts = custodianData.custodianA?.data.tables.accounts || []
    const custodianBAccounts = custodianData.custodianB?.data.tables.accounts || []

    // Get unique clients from both custodians by matching on name (first_name + last_name)
    const uniqueClientNames = new Set<string>()
    const clientNameMap = new Map<string, { clientA: any; clientB: any }>()

    // Process Custodian A clients
    custodianAClients.forEach((client: any) => {
      const clientName = `${client.first_name} ${client.last_name}`
      uniqueClientNames.add(clientName)
      clientNameMap.set(clientName, { clientA: client, clientB: null })
    })

    // Process Custodian B clients and merge with existing entries
    custodianBClients.forEach((client: any) => {
      const clientName = `${client.first_name} ${client.last_name}`
      uniqueClientNames.add(clientName)
      const existing = clientNameMap.get(clientName)
      if (existing) {
        existing.clientB = client
      } else {
        clientNameMap.set(clientName, { clientA: null, clientB: client })
      }
    })

    const clientsList = Array.from(uniqueClientNames).slice(0, 5) // Show first 5 unique clients

    return (
      <div className="merged-view-container">
        <h3 className="section-title">Merged Portfolio View</h3>
        <p className="merged-view-subtitle">Combined Holdings, Portfolio & Securities data for each client</p>

        {clientsList.map((clientName) => {
          const { clientA, clientB } = clientNameMap.get(clientName) || { clientA: null, clientB: null }
          const client = clientA || clientB

          if (!client) return null

          // Get accounts for this client from both custodians using their respective client identifiers
          const accountsA = clientA ? custodianAAccounts.filter((a: any) => a.client_identifier === clientA.client_identifier) : []
          const accountsB = clientB ? custodianBAccounts.filter((a: any) => a.client_identifier === clientB.client_identifier) : []

          // Get holdings for these accounts
          const holdingsA = custodianAHoldings.filter((h: any) =>
            accountsA.some((a: any) => a.id === h.account_id)
          )
          const holdingsB = custodianBHoldings.filter((h: any) =>
            accountsB.some((a: any) => a.id === h.account_id)
          )

          // Get portfolio summaries
          const portfoliosA = custodianAPortfolios.filter((p: any) =>
            accountsA.some((a: any) => a.id === p.account_id)
          )
          const portfoliosB = custodianBPortfolios.filter((p: any) =>
            accountsB.some((a: any) => a.id === p.account_id)
          )

          // Calculate totals
          const totalMarketValueA = portfoliosA.reduce((sum: number, p: any) =>
            sum + parseFloat(p.total_market_value || 0), 0
          )
          const totalMarketValueB = portfoliosB.reduce((sum: number, p: any) =>
            sum + parseFloat(p.total_market_value || 0), 0
          )


          return (
            <div key={clientName} className="client-merged-card">
              <div className="client-merged-header">
                <div className="client-info">
                  <h4 className="client-name">
                    {client.first_name} {client.last_name}
                  </h4>
                  <p className="client-details">
                    {client.company} • {client.designation}
                  </p>
                </div>
              </div>

              <div className="custodian-comparison-grid">
                {/* Custodian A Card */}
                <div className="custodian-card custodian-a-card">
                  <div className="custodian-card-header">
                    <h5 className="custodian-name">Custodian A</h5>
                    <span className="custodian-badge custodian-a-badge">{accountsA.length} Accounts</span>
                  </div>

                  <div className="portfolio-summary-section">
                    <h6 className="section-heading">Asset Category Allocation</h6>

                    {(() => {
                      // Calculate asset allocation based on portfolio summaries
                      const equityValue = portfoliosA.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.equity_percentage || 0) / 100)), 0
                      )
                      const bondValue = portfoliosA.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.fixed_income_percentage || 0) / 100)), 0
                      )
                      const cashValue = portfoliosA.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.cash_percentage || 0) / 100)), 0
                      )

                      const equityPercentage = totalMarketValueA > 0 ? (equityValue / totalMarketValueA) * 100 : 0
                      const bondPercentage = totalMarketValueA > 0 ? (bondValue / totalMarketValueA) * 100 : 0
                      const cashPercentage = totalMarketValueA > 0 ? (cashValue / totalMarketValueA) * 100 : 0

                      return (
                        <>
                          <div className="allocation-bar-container">
                            {equityPercentage > 0 && (
                              <div
                                className="allocation-segment equity"
                                style={{ width: `${equityPercentage}%` }}
                                data-tooltip={`Equity: ${equityPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                            {bondPercentage > 0 && (
                              <div
                                className="allocation-segment bond"
                                style={{ width: `${bondPercentage}%` }}
                                data-tooltip={`Bond: ${bondPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                            {cashPercentage > 0 && (
                              <div
                                className="allocation-segment cash"
                                style={{ width: `${cashPercentage}%` }}
                                data-tooltip={`Cash: ${cashPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                          </div>

                          <div className="allocation-table">
                            <div className="allocation-table-header">
                              <span className="header-category">ASSET CATEGORY</span>
                              <span className="header-value">MARKET VALUE</span>
                              <span className="header-allocation">ALLOCATION</span>
                            </div>
                            <div className="allocation-table-body">
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot equity"></span>
                                  Equity
                                </span>
                                <span className="category-value">${equityValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{equityPercentage.toFixed(2)}%</span>
                              </div>
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot bond"></span>
                                  Bond
                                </span>
                                <span className="category-value">${bondValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{bondPercentage.toFixed(2)}%</span>
                              </div>
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot cash"></span>
                                  Cash
                                </span>
                                <span className="category-value">${cashValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{cashPercentage.toFixed(2)}%</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>

                  {holdingsA.length > 0 && (
                    <div className="holdings-section">
                      <h6 className="section-heading">Top Holdings</h6>
                      <div className="holdings-list">
                        {holdingsA.slice(0, 3).map((holding: any, idx: number) => {
                          const security = custodianASecurities.find((s: any) => s.id === holding.security_id)
                          return (
                            <div key={idx} className="holding-item">
                              <div className="holding-info">
                                <span className="holding-name">
                                  {security?.security_name || 'Unknown'}
                                </span>
                                <span className="holding-symbol">
                                  {security?.symbol || 'N/A'}
                                </span>
                              </div>
                              <div className="holding-values">
                                <span className="holding-quantity">
                                  {parseFloat(holding.quantity || 0).toLocaleString()} shares
                                </span>
                                <span className="holding-value">
                                  ${parseFloat(holding.market_value || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {holdingsA.length === 0 && (
                    <div className="empty-state">
                      <p>No holdings data available</p>
                    </div>
                  )}
                </div>

                {/* Custodian B Card */}
                <div className="custodian-card custodian-b-card">
                  <div className="custodian-card-header">
                    <h5 className="custodian-name">Custodian B</h5>
                    <span className="custodian-badge custodian-b-badge">{accountsB.length} Accounts</span>
                  </div>

                  <div className="portfolio-summary-section">
                    <h6 className="section-heading">Asset Category Allocation</h6>

                    {(() => {
                      // Calculate asset allocation based on portfolio summaries
                      const equityValue = portfoliosB.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.equity_percentage || 0) / 100)), 0
                      )
                      const bondValue = portfoliosB.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.fixed_income_percentage || 0) / 100)), 0
                      )
                      const cashValue = portfoliosB.reduce((sum: number, p: any) =>
                        sum + (parseFloat(p.total_market_value || 0) * (parseFloat(p.cash_percentage || 0) / 100)), 0
                      )

                      const equityPercentage = totalMarketValueB > 0 ? (equityValue / totalMarketValueB) * 100 : 0
                      const bondPercentage = totalMarketValueB > 0 ? (bondValue / totalMarketValueB) * 100 : 0
                      const cashPercentage = totalMarketValueB > 0 ? (cashValue / totalMarketValueB) * 100 : 0

                      return (
                        <>
                          <div className="allocation-bar-container">
                            {equityPercentage > 0 && (
                              <div
                                className="allocation-segment equity"
                                style={{ width: `${equityPercentage}%` }}
                                data-tooltip={`Equity: ${equityPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                            {bondPercentage > 0 && (
                              <div
                                className="allocation-segment bond"
                                style={{ width: `${bondPercentage}%` }}
                                data-tooltip={`Bond: ${bondPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                            {cashPercentage > 0 && (
                              <div
                                className="allocation-segment cash"
                                style={{ width: `${cashPercentage}%` }}
                                data-tooltip={`Cash: ${cashPercentage.toFixed(2)}%`}
                              ></div>
                            )}
                          </div>

                          <div className="allocation-table">
                            <div className="allocation-table-header">
                              <span className="header-category">ASSET CATEGORY</span>
                              <span className="header-value">MARKET VALUE</span>
                              <span className="header-allocation">ALLOCATION</span>
                            </div>
                            <div className="allocation-table-body">
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot equity"></span>
                                  Equity
                                </span>
                                <span className="category-value">${equityValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{equityPercentage.toFixed(2)}%</span>
                              </div>
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot bond"></span>
                                  Bond
                                </span>
                                <span className="category-value">${bondValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{bondPercentage.toFixed(2)}%</span>
                              </div>
                              <div className="allocation-row">
                                <span className="category-name">
                                  <span className="category-dot cash"></span>
                                  Cash
                                </span>
                                <span className="category-value">${cashValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <span className="category-allocation">{cashPercentage.toFixed(2)}%</span>
                              </div>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>

                  {holdingsB.length > 0 && (
                    <div className="holdings-section">
                      <h6 className="section-heading">Top Holdings</h6>
                      <div className="holdings-list">
                        {holdingsB.slice(0, 3).map((holding: any, idx: number) => {
                          const security = custodianBSecurities.find((s: any) => s.id === holding.security_id)
                          return (
                            <div key={idx} className="holding-item">
                              <div className="holding-info">
                                <span className="holding-name">
                                  {security?.security_name || 'Unknown'}
                                </span>
                                <span className="holding-symbol">
                                  {security?.symbol || 'N/A'}
                                </span>
                              </div>
                              <div className="holding-values">
                                <span className="holding-quantity">
                                  {parseFloat(holding.quantity || 0).toLocaleString()} shares
                                </span>
                                <span className="holding-value">
                                  ${parseFloat(holding.market_value || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {holdingsB.length === 0 && (
                    <div className="empty-state">
                      <p>No holdings data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )
        })}

        <div className="merged-view-footer">
          <p>Showing first 5 clients with merged portfolio, holdings, and securities data</p>
        </div>
      </div>
    )
  }

  return (
    <div className="banks-info-container">
      {/* Header */}
      <div className="banks-info-header">
        <div className="banks-info-header-left">
          <h2 className="banks-info-title">Custodian Comparison</h2>
          <p className="banks-info-subtitle">
            Side-by-side comparison of Custodian A and Custodian B data
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => handleExport('custodian_a')}
            disabled={exportLoading === 'custodian_a'}
            className="data-type-btn"
            style={{
              backgroundColor: exportLoading === 'custodian_a' ? '#f3f4f6' : '#3b82f6',
              color: exportLoading === 'custodian_a' ? '#6b7280' : 'white',
              cursor: exportLoading === 'custodian_a' ? 'not-allowed' : 'pointer',
              opacity: exportLoading === 'custodian_a' ? 0.6 : 1
            }}
          >
            {exportLoading === 'custodian_a' ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #6b7280',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }}></div>
                Exporting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export Custodian A
              </>
            )}
          </button>

          <button
            onClick={() => handleExport('custodian_b')}
            disabled={exportLoading === 'custodian_b'}
            className="data-type-btn"
            style={{
              backgroundColor: exportLoading === 'custodian_b' ? '#f3f4f6' : '#10b981',
              color: exportLoading === 'custodian_b' ? '#6b7280' : 'white',
              cursor: exportLoading === 'custodian_b' ? 'not-allowed' : 'pointer',
              opacity: exportLoading === 'custodian_b' ? 0.6 : 1
            }}
          >
            {exportLoading === 'custodian_b' ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #6b7280',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }}></div>
                Exporting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export Custodian B
              </>
            )}
          </button>
        </div>
      </div>

      {/* Data Type Selector */}
      <div className="data-type-selector">
        <button
          className={`data-type-btn ${activeDataType === 'clients' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('clients')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Customer Data
        </button>
        <button
          className={`data-type-btn ${activeDataType === 'accounts' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('accounts')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="3" width="15" height="13"/>
            <path d="M16 8h4a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-4"/>
            <circle cx="9" cy="9" r="2"/>
          </svg>
          Accounts Data
        </button>
        <button
          className={`data-type-btn ${activeDataType === 'merged' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('merged')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 6h16M4 12h16M4 18h16"/>
            <path d="M9 6v12M15 6v12"/>
          </svg>
          Portfolio Data
        </button>
        {/* <button
          className={`data-type-btn ${activeDataType === 'holdings' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('holdings')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          Holdings Data
        </button> */}
        {/* <button
          className={`data-type-btn ${activeDataType === 'portfolios' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('portfolios')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3v18h18"/>
            <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
          </svg>
          Portfolio Data
        </button> */}
        {/* <button
          className={`data-type-btn ${activeDataType === 'securities' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('securities')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1"/>
            <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          Securities Data
        </button> */}
        {/* <button
          className={`data-type-btn ${activeDataType === 'prices' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('prices')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
          </svg>
          Prices Data
        </button> */}
        {/* <button
          className={`data-type-btn ${activeDataType === 'contact_info' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('contact_info')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          Contact Info
        </button> */}
      </div>

      {/* Export Success/Error Messages */}
      {(exportSuccess || exportError) && (
        <div style={{ marginBottom: '24px' }}>
          {exportSuccess && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              position: 'relative'
            }}>
              <button
                onClick={clearExportNotifications}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  color: '#6b7280',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#0369a1', margin: '0 0 8px 0' }}>
                ✅ CSV Export Successful!
              </h4>
              <p style={{ fontSize: '12px', color: '#0369a1', margin: '0 0 8px 0' }}>
                {exportSuccess.message}
              </p>
              <div style={{ fontSize: '11px', color: '#0284c7' }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>File:</strong> {exportSuccess.data.original_filename}
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Size:</strong> {(exportSuccess.data.file_size / 1024).toFixed(1)} KB
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <strong>S3 URL:</strong>
                  <code style={{
                    fontSize: '10px',
                    backgroundColor: '#e0f2fe',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {exportSuccess.data.s3_url}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(exportSuccess.data.s3_url)}
                    style={{
                      padding: '2px 4px',
                      fontSize: '10px',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                    title="Copy S3 URL"
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Export Error Message */}
          {exportError && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #f87171',
              borderRadius: '6px'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', margin: '0 0 4px 0' }}>
                ❌ Export Failed
              </h4>
              <p style={{ fontSize: '12px', color: '#dc2626', margin: 0 }}>
                {exportError}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Render Merged View or Comparison Table based on activeDataType */}
      {activeDataType === 'merged' ? renderMergedView() : (
        <div className="comparison-section">
          <h3 className="section-title">
            {activeDataType.charAt(0).toUpperCase() + activeDataType.slice(1)} Data Comparison
          </h3>


          <div className="comparison-table-container">
            <div className="comparison-table-wrapper">
              <table className="comparison-table">
                <thead>
                  <tr>
                    <th className="record-header">#</th>
                    <th className="field-header">Field</th>
                    <th className="bank-header bank-a-header">Custodian A</th>
                    <th className="bank-header bank-b-header">Custodian B</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Show first 10 records for comparison with comprehensive field comparison */}
                  {Array.from({ length: Math.min(Math.max(custodianAData.length, custodianBData.length), 10) }).map((_, recordIndex) => {
                    const custodianAItem = custodianAData[recordIndex]
                    const custodianBItem = custodianBData[recordIndex]

                    if (!custodianAItem && !custodianBItem) return null

                    const comprehensiveRowData = getComprehensiveRowData(custodianAItem, custodianBItem)

                    return comprehensiveRowData.map((fieldData, fieldIndex) => (
                      <tr key={`${recordIndex}-${fieldIndex}`} className={`record-row ${recordIndex % 2 === 0 ? 'even-record' : 'odd-record'} ${fieldIndex === 0 && recordIndex > 0 ? 'record-first-row' : ''}`}>
                        {fieldIndex === 0 && (
                          <td rowSpan={comprehensiveRowData.length} className="record-number">
                            {recordIndex + 1}
                          </td>
                        )}
                        <td className="field-name">
                          {fieldData.displayName}
                        </td>
                        <td className={`data-cell bank-a-data ${!custodianAItem ? 'no-data' : ''} ${fieldData.isDifferent ? 'different-value' : ''}`}>
                          {custodianAItem ? fieldData.custodianAValue : <span className="no-data-dash">—</span>}
                        </td>
                        <td className={`data-cell bank-b-data ${!custodianBItem ? 'no-data' : ''} ${fieldData.isDifferent ? 'different-value' : ''}`}>
                          {custodianBItem ? fieldData.custodianBValue : <span className="no-data-dash">—</span>}
                        </td>
                      </tr>
                    ))
                  }).flat().filter(Boolean)}
                </tbody>
              </table>
            </div>
          </div>

          <div className="comparison-footer">
            <p>Showing first 10 records for comparison. Different values are highlighted with colored backgrounds.</p>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          tr.even-record td { background-color: #f8fafc; }
          tr.odd-record td  { background-color: #ffffff; }

          /* Separator line between record groups */
          tr.record-first-row td {
            border-top: 2px solid #a6a8ab !important;
          }

          td.record-number {
            vertical-align: top;
            font-weight: 700;
            text-align: center;
            font-size: 12px;
            min-width: 32px;
          }

          td.bank-a-data.different-value { background-color: #fef2f2 !important; color: #dc2626 !important; font-weight: 500; }
          td.bank-b-data.different-value { background-color: #eff6ff !important; color: #1e40af !important; font-weight: 500; }
        `}
      </style>
    </div>
  )
}

export default CustodianInfoDisplay